import toast from "@components/Toast";
import { Button, Checkbox, TextInput } from "@components/library";
import { FONTS } from "@constants";
import { RootState } from "@redux/store";
import { editRequestComment } from "@requests/requestComments";
import { useMutation } from "@tanstack/react-query";
import { RequestCommentPartial } from "@tsTypes/index";
import { Dispatch, SetStateAction, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { Cancel, CheckboxContainer, Submit } from "./RequestDiscussionSubmitForm";

interface Props {
  comment: RequestCommentPartial;
  onSuccess: () => Promise<void>;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}

const RequestDiscussionEditForm = ({ comment, onSuccess, setIsEditing }: Props) => {
  const [text, setText] = useState(comment.text ?? "");
  const [isOpen, setIsOpen] = useState(comment.open ?? true);

  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);

  const mutation = useMutation({
    mutationFn: () => editRequestComment(comment.id, text, isOpen),
    onSuccess: async () => {
      setText("");
      toast.success("Response edited!");
      await onSuccess();
    },
    meta: {
      useGlobalErrorHandler: true,
    },
  });

  const handleSubmitComment = () => {
    mutation.mutate();
  };

  const answering = currentUser.role === 1;

  return (
    <EditContainer>
      <TextInput
        value={text}
        labelFont={FONTS.HEADING_5_SEMIBOLD}
        placeholder="Write your answer"
        onChange={(e) => setText(e.target.value)}
        textarea
        startingHeight="150px"
        resize="none"
      />
      <CheckboxContainer>
        {answering && (
          <Checkbox
            label="Allow researchers to respond to your comment"
            isChecked={isOpen}
            onChange={(e) => setIsOpen(e.target.checked)}
            size="sm"
          />
        )}
      </CheckboxContainer>
      <Submit isEditing>
        <Button
          size="sm"
          margin="0 0 0 16px"
          onClick={handleSubmitComment}
          disabled={mutation.isLoading || text?.length === 0}
        >
          Submit
        </Button>
        <Cancel onClick={() => setIsEditing(false)}>Cancel</Cancel>
      </Submit>
    </EditContainer>
  );
};

export default RequestDiscussionEditForm;

const EditContainer = styled.div`
  width: 100%;
`;
